import { TotalsRow } from 'components/TableTotals'
import { getPrice2decimal } from 'utils'

import { type ILoanChargeSummary, LoanChargeGroupBy } from './types'

/** Currency cell: "$1,234.56", always to the cent so a total can be tied out. */
const money = (value: number) => getPrice2decimal(value, false, true, true)

/**
 * A charges section totalled one line per charge type - three Extension Fees owing $X, four Forced
 * Placed Insurance owing $Y - so what is owed on each kind of fee reads off the screen instead of
 * having to be added up down the grid.
 *
 * The figures are the server's, over every charge the grid's filters select rather than the page in
 * hand, and each charge is accrued forward the same way the grid accrues it, so the Total Due here
 * and the section's "Total Amount" are the same number.
 */
export const ChargesSummary = ({
  summary,
  groupBy,
}: {
  summary: ILoanChargeSummary | null
  groupBy: LoanChargeGroupBy
}) => {
  const groups = summary?.groups || []
  const totals = summary?.totals
  const labelHeader = groupBy === LoanChargeGroupBy.Category ? 'Category' : 'Charge Type'

  return (
    <div className="parties-container overflow-auto max-h-[70vh] mb-6 mt-6 shadow-md sm:rounded-lg">
      <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
        <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
              {labelHeader}
            </th>
            <th scope="col" className="px-2 py-3 text-right sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
              Charges
            </th>
            <th scope="col" className="px-2 py-3 text-right sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
              Original Balance
            </th>
            <th scope="col" className="px-2 py-3 text-right sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
              Unpaid Balance
            </th>
            <th scope="col" className="px-2 py-3 text-right sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
              Accrued Interest
            </th>
            <th scope="col" className="px-2 py-3 text-right sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
              Total Due
            </th>
          </tr>
        </thead>
        <tbody className="text-[14.5px] text-gray-900">
          {groups.map((group, index) => (
            <tr key={group.label || index} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
              <td className="px-6 py-3">{group.label}</td>
              <td className="px-2 py-3 text-right tabular-nums">{group.count}</td>
              <td className="px-2 py-3 text-right tabular-nums">{money(group.originalBalance)}</td>
              <td className="px-2 py-3 text-right tabular-nums">{money(group.unpaidBalance)}</td>
              <td className="px-2 py-3 text-right tabular-nums">{money(group.accruedInterest)}</td>
              <td className="px-2 py-3 text-right tabular-nums">{money(group.totalDue)}</td>
            </tr>
          ))}
          {!groups.length && (
            <tr className="border-b">
              <td className="px-6 py-3 text-gray-500" colSpan={6}>
                No charges to summarize.
              </td>
            </tr>
          )}
        </tbody>
        {!!groups.length && totals && (
          <TotalsRow
            sticky
            tdClass="px-2 py-3"
            cells={[
              { value: 'Totals', className: 'px-6 py-3 whitespace-nowrap' },
              { value: <div className="text-right tabular-nums">{totals.count}</div> },
              totals.originalBalance,
              totals.unpaidBalance,
              totals.accruedInterest,
              totals.totalDue,
            ]}
          />
        )}
      </table>
    </div>
  )
}
